import { useState } from 'react';
import { GitBranchIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import type { SalesOrderV3Detail } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { OrderLifecycleStageBadge } from 'src/components/sales-order-lifecycle-stage/OrderLifecycleStageBadge';
import { useSalesOrderSplitGroup } from '@/api/rpc/hooks/accounts-receivable/useSalesOrdersV3';

/**
 * "⑂ Split ×N" header chip shown when the draft belongs to a split group. Opens
 * a popover listing every sibling (the original + each split-out child) with its
 * split rule, lifecycle stage, and line count, linking to each. The member list
 * is fetched lazily — only once the popover opens — so non-split drafts (the
 * common case) and unopened badges never hit the API.
 */
export function SplitGroupBadge({ draft }: { draft: SalesOrderV3Detail }) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const { members, isLoading } = useSalesOrderSplitGroup(draft.id, {
    enabled: open,
  });

  // Only part of a split (the group has more than just this draft) shows the badge.
  if (!draft.splitGroupId || draft.splitGroupSize <= 1) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={gt('Show split orders')}>
          <Badge
            variant="outline"
            className="cursor-pointer gap-1 border-blue-200 text-blue-700"
          >
            <GitBranchIcon className="size-3" />
            <T>Split</T> ×{draft.splitGroupSize}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <p className="px-1 pb-1.5 text-xs font-medium text-muted-foreground">
          <T>Orders in this split</T>
        </p>
        {isLoading ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            <T>Loading…</T>
          </p>
        ) : (
          <ul className="space-y-0.5">
            {members.map((m, i) => {
              const label = gt('Split {n}', { n: String(i + 1) });
              const meta = gt('{count} items', {
                count: String(m.lineItemCount),
              });
              const body = (
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm">{label}</div>
                    <div className="text-[0.7rem] text-muted-foreground">
                      {m.isCurrent ? `${meta} · ${gt('current')}` : meta}
                    </div>
                  </div>
                  <OrderLifecycleStageBadge stage={m.lifecycleStage} />
                </div>
              );
              return (
                <li key={m.id}>
                  {m.isCurrent ? (
                    <div className="rounded-md bg-muted px-2 py-1.5">
                      {body}
                    </div>
                  ) : (
                    <Link
                      to="/sales-orders/$draftId"
                      params={{ draftId: m.id }}
                      className="block rounded-md px-2 py-1.5 hover:bg-muted"
                      onClick={() => setOpen(false)}
                    >
                      {body}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
